import {
    format,
    formatDistance,
    startOfToday,
    startOfYesterday,
    add,
    getHours,
    getMinutes,
    isValid,
    subDays
} from 'date-fns';
import React from 'react';
import {getEmployeeName, publishClientSideMetrics} from "../handler/ParentDataHandler";
import {getDateLocale} from "../handler/LocaleHandler";
import {
    AMAZON_SUFFIX, AT_STATION, BETA,
    DATE_TIME_FORMAT,
    DELIVERY,
    ENVIRONMENT, FIVE_AND_THREE_HOUR, GAMMA, INDUCT, INTEG, MASTER,
    ONE_HOUR,
    PACKAGE_TYPE_CORE,
    PACKAGE_TYPE_GROCERY,
    REGION,
    STAGE
} from "../commons/constants/CommonConstants";
import {oldTimeZoneMapper, validateEmail} from "../commons/CommonUtils";
import {UseCaseCode} from "./UseCase";
import { FormattedMessage } from 'react-intl';
import { ActualScanToPackageStateMapping } from '../commons/dataStore/PackageResultsData';
import Link from '@amzn/meridian/link';

export const INDUCTED = "INDUCTED";
export const INDUCT_PENDING = "INDUCT_PENDING"
export const STOWED = "STOWED";
export const SORT = "SORT";
export const PICKED = "PICKED";
export const STAGED = "STAGED";
export const DEPARTED = "DEPARTED";
export const HELD = "HELD";
export const EXCEPTIONS_PS = "PROBLEM SOLVE";
export const EXCEPTIONS_DWELLING = "EXCEPTIONS_DWELLING";
export const EXCEPTIONS_PROBLEM_SOLVE_PACKAGE_STATUS_LIST = ["PROBLEM_SOLVE", "AT_WRONG_STATION"];
export const INDUCT_PENDING_PACKAGE_STATUS_LIST = ["ARRIVED", "Received (DS -> Customer)", "DELAYED_AT_STATION", "Held On Ead", "Marked for Reprocess"];
export const INDUCTED_PACKAGE_STATUS_LIST = ["INDUCTED", "INDUCT"];
export const STOWED_PACKAGE_STATUS_LIST = ["SORT", "STOWED"];
export const PICKED_PACKAGE_STATUS_LIST = ["PICKED"];
export const STAGED_PACKAGE_STATUS_LIST = ["STAGE", "STAGED", "Staged (DS -> Customer)"];
export const DEPARTED_PACKAGE_STATUS_LIST = ["DEPARTED", "In Transit (DS -> Customer)", "In Transit (DS -> XPT)"];
export const HELD_PACKAGE_STATUS_LIST = ['HELD'];
export const PROBLEM_SOLVE_PACKAGE_STATUS_LIST = ['Induct Problem Solve', 'Stow Problem Solve', 'Pick Problem Solve', 'Depart Problem Solve'];
export const DWELL_PACKAGE_STATUS = {
    INDUCTED: INDUCTED,
    INDUCT: INDUCT,
    STOWED: STOWED,
    INDUCT_PENDING: INDUCT_PENDING,
    SORT: SORT,
    PICKED: PICKED,
    STAGED: STAGED,
    EXCEPTIONS_PS: EXCEPTIONS_PS,
    EXCEPTIONS_DWELLING: EXCEPTIONS_DWELLING,
    DEPARTED: DEPARTED,
    HELD: HELD,
}

export const PACKAGE_STATUSES_PENDING_INDUCT = ['Arrived', 'Received', 'Held', 'Marked for Reprocess', 'Induct Problem Solve','Received (DS -> Customer)'];
export const PACKAGE_STATUSES_INDUCTED = ['Inducted'];
export const PACKAGE_STATUSES_STOWED = ['Stowed'];
export const PACKAGE_STATUSES_STOW_JACKPOT_BUFFERED = ['Stow Buffered', 'Jackpot Buffered'];
export const PACKAGE_STATUSES_PICK = ['Picked'];
export const PACKAGE_STATUSES_STAGE = ['Staged (DS -> Customer)'];
export const PACKAGE_STATUSES_PROBLEM_SOLVE = ['Stow Problem Solve', 'Jackpot Buffered', 'Pick Problem Solve', 'Depart Problem Solve'];
export const PACKAGE_STATUSES_DEPART = ['In Transit (DS -> Customer)', 'In Transit (DS -> XPT)'];
export const PACKAGE_STATUSES_HELD = ['Held'];

export const STATUS_RULES = [
    {
        list: PROBLEM_SOLVE_PACKAGE_STATUS_LIST,
        result: DWELL_PACKAGE_STATUS.EXCEPTIONS_PS
    },
    {
        list: INDUCT_PENDING_PACKAGE_STATUS_LIST,
        result: DWELL_PACKAGE_STATUS.INDUCT_PENDING
    },
    {
        list: INDUCTED_PACKAGE_STATUS_LIST,
        result: DWELL_PACKAGE_STATUS.INDUCTED
    },
    {
        list: STOWED_PACKAGE_STATUS_LIST,
        result: DWELL_PACKAGE_STATUS.STOWED
    },
    {
        list: PICKED_PACKAGE_STATUS_LIST,
        result: DWELL_PACKAGE_STATUS.PICKED
    },
    {
        list: STAGED_PACKAGE_STATUS_LIST,
        result: DWELL_PACKAGE_STATUS.STAGED
    },
    {
        list: DEPARTED_PACKAGE_STATUS_LIST,
        result: DWELL_PACKAGE_STATUS.DEPARTED
    },
    {
        list: HELD_PACKAGE_STATUS_LIST,
        result: DWELL_PACKAGE_STATUS.HELD
    }
];

export function getPackageStatus(state, packageType) {
    let actualPackageStatus = state;
    
    if (packageType && AT_STATION === actualPackageStatus && DELIVERY === packageType) {
        actualPackageStatus = INDUCTED;
    }

    const normalizedStatus = actualPackageStatus.toLowerCase();
    
    const matchingRule = STATUS_RULES.find(rule => 
        rule.list.some(status => status.toLowerCase() === normalizedStatus)
    );

    return matchingRule ? matchingRule.result : actualPackageStatus;
}

export const getPackageOverallMetrics = (packageMetricData) => {
    const metrics = {
            totalPackages: 0,
            inductPendingPackages: 0,
            inductedPackages: 0,
            stowedPackages: 0,
            pickedPackages: 0,
            stagedPackages: 0,
            departedPackages: 0,
            heldPackages: 0,
            exceptionProblemSolvePackages: 0
    }
    if (packageMetricData) {
        let packageStatusAndCountMap = getPackageStatusAndCountMap(packageMetricData);
        
        Object.entries(packageStatusAndCountMap).forEach(([key, val]) => {
            let packageStatus = getPackageStatus(key, null)
            metrics.totalPackages += val;
            switch (packageStatus) {
                case DWELL_PACKAGE_STATUS.INDUCTED:
                    metrics.inductedPackages += val;
                    break;
                case DWELL_PACKAGE_STATUS.INDUCT_PENDING:
                    metrics.inductPendingPackages += val;
                    break;
                case DWELL_PACKAGE_STATUS.STOWED:
                case DWELL_PACKAGE_STATUS.SORT:
                    metrics.stowedPackages += val;
                    break;
                case DWELL_PACKAGE_STATUS.PICKED:
                    metrics.pickedPackages += val;
                    break;
                case DWELL_PACKAGE_STATUS.STAGED:
                    metrics.stagedPackages += val;
                    break;
                case DWELL_PACKAGE_STATUS.EXCEPTIONS_PS:
                    metrics.exceptionProblemSolvePackages += val;
                    break;
                case DWELL_PACKAGE_STATUS.DEPARTED:
                    metrics.departedPackages += val;
                    break;
                case DWELL_PACKAGE_STATUS.HELD:
                    metrics.heldPackages += val;
                    break;
                default:
                    break;
            }
        });
    }
    return metrics
}

export const getRequestFilterForDwellingView = (tabSelected, addShipOption) => {
    let requestFilters = {
        CPT: [],
        cycles: []
    }
    if (addShipOption) {
        requestFilters['shipOption'] = getShipOption(tabSelected);
    }
    return requestFilters;
}

export const getShipOption = (tabSelected) => {
    let shipOption = []
    if (tabSelected === FIVE_AND_THREE_HOUR) {
        shipOption = ['rush', 'rush-rapid']
    }
    else if (tabSelected === ONE_HOUR){
        shipOption = ['rush-expr']
    }
    return shipOption;
}

export function getPackageDrilDownRequestFilter(metricData, tabSelected, packageStatus, stationCode, addShipOption) {
    // 24 hrs has 1440 minutes
    let dwellTime = 1440;
    if (metricData && JSON.stringify(metricData).match(/[0-9]*_MIN/g)) {
        dwellTime = parseInt(JSON.stringify(metricData).match(/[0-9]*_MIN/g)[0].replace("_MIN", ""), 10);
        if (isNaN(dwellTime)) {
            dwellTime = 1440;
        }
    }
    let selectedFilterValues = {};
    var stationTimeZone = stationCode;
    var timeZone = stationTimeZone ? oldTimeZoneMapper(stationTimeZone) : undefined;
    selectedFilterValues = {
        CPT: [],
        cycles: [
            'No Cycle', 'CYCLE_1'
        ],
        dwellEndTime: '0_MIN',
        dwellStartTime: dwellTime + '_MIN',
        page: "DWELL",
        packageStatuses: [
            ...packageStatus
        ],
        packageTypes: ['Core Volume'],
        stationArrivalTime: []
    }
    if (addShipOption) {
        let shipOption = getShipOption(tabSelected);
        selectedFilterValues["shipOption"] = shipOption;
    }

    
    if (dwellTime !== 0) {
        let currentDate = new Date(new Date(Date.now()).toLocaleString('en-US', {timeZone: timeZone}));
        let resetDate = new Date(new Date(Date.now() - dwellTime*60*1000).toLocaleString('en-US', {timeZone: timeZone}));

        let endDate;

        if (
            currentDate.getHours() < resetDate.getHours()
            || (
                currentDate.getHours() === resetDate.getHours()
                && currentDate.getMinutes() < resetDate.getMinutes()
            )
        ) {
            endDate = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), resetDate.getHours(), resetDate.getMinutes(), 0));
        } else {
            endDate = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1, resetDate.getHours(), resetDate.getMinutes(), 0));
        }
        selectedFilterValues["lastUpdatedStart"] = new Date(endDate.getTime() - 24*60*60*1000);
        selectedFilterValues["lastUpdatedEnd"] = new Date(endDate.getTime());
    }
    selectedFilterValues["enableDwellFilter"] = false;
    return selectedFilterValues;
}


export function dwellDetailedUiFilterOption(viewModelFilter, packageData, filter, key) {
    if (filter === "exceptionDwelling") {
        if (viewModelFilter.exceptionDwelling) {
            return isExceptionDwellingPackage(packageData);
        }
        return true;
    }
    if ( viewModelFilter[filter].length !== undefined && viewModelFilter[filter].length === 0) {
        return true;
    }
    let result = false;
    viewModelFilter[filter].forEach(filterValue => {
        if (packageData[key] === undefined
            || (filter === "packageStatus" && 
                getDwellPackageStatusForFilter(packageData.state, packageData.packageType, filterValue))
            || (filter === "packageStatus" && 
                filterValue === EXCEPTIONS_DWELLING && 
                isExceptionDwellingPackage(packageData))
            || (filter === "packageScheduledDeliveryWindow" && 
                getPackageScheduleDeliveryWindow(packageData) === filterValue)
            || (filter === "packageType" && 
                getDwellPackageTypeForFilter(packageData[key], filterValue))) {
            result = true;
        }
    })
    return result;
}
export function getPackageScheduleDeliveryWindow(packageData) {
    let deliveryWindow;
    if (packageData.scheduledDeliveryStart !== "" && packageData.scheduledDeliveryEnd !== "") {
        let deliveryStartTimeArray = (packageData.scheduledDeliveryStart.split(' ')[1]).split(':');
        let deliveryEndTimeArray = (packageData.scheduledDeliveryEnd.split(' ')[1]).split(':');

        let deliveryStartTime = deliveryStartTimeArray[0] + ":" + deliveryStartTimeArray[1];
        let deliveryEndTime = deliveryEndTimeArray[0] + ":" + deliveryEndTimeArray[1];
        deliveryWindow = deliveryStartTime + "-" + deliveryEndTime;
    }
    return deliveryWindow;
}
export function getDwellPackageStatusForFilter (packageStatus, packageType, filter) {
    let stat = getPackageStatus(packageStatus, packageType);
    stat = stat.toLowerCase();
    return stat.toLowerCase().startsWith(filter.toLowerCase())
};
export function getDwellPackageTypeForFilter (isGrocery, filter) {
    return (isGrocery && filter === PACKAGE_TYPE_GROCERY) || 
        (!isGrocery && filter === PACKAGE_TYPE_CORE)
};
export function getDwellPackageSubStatusForFilter (packageStatus, packageType, filter) {
    let stat = getFormattedMessageForState(packageStatus, packageType);
    return stat.toLowerCase().startsWith(filter.toLowerCase())
};
export function getDwellPackageSubStatusListForFilter (packageStatus, packageType, filter) {
    if(!filter || filter.length === 0) {
        return true;
    }
    let stat = getFormattedMessageForState(packageStatus, packageType);
    stat = stat.toLowerCase();
    let filterMatched = false;
    filter.forEach((item) => {
        if (stat.toLowerCase().startsWith(item.toLowerCase())) {
            filterMatched = true;
        }
    })
    return filterMatched;
};
export const DWELLING_TIME_THRESHOLD = 200;
export function isExceptionDwellingPackage (pckg) {
    if (pckg.minutesInState) {
        return Number(pckg.minutesInState) > DWELLING_TIME_THRESHOLD;
    }
    return false;
}
// pass metric result data received in getDwellingPackageData api response
export function getPackageStatusAndCountMap(packageMetricData) {
    return packageMetricData.reduce((packageStatusAndCountMap, metric) => {
        let packageStatus = metric.packageStatus;
        if (metric.metaDataMap && metric.metaDataMap.direction) {
            packageStatus = packageStatus + " (" + metric.metaDataMap.direction+")";
        }
        const totalValue = Object.values(metric.columnToViewDataMap)
            .reduce((sum, item) => sum + parseInt(item.value || 0, 10), 0);
        
        packageStatusAndCountMap[packageStatus] = totalValue;
        return packageStatusAndCountMap;
    }, {});
}

/**
 * Returns dwell packages status indicator color
 *
 * @param packageStatus status of package
 * @returns color
 */
export function getDwellPackageStatusColor(packageStatus, packageType) {
    let actualPackageStatus = packageStatus;
    if (AT_STATION === packageStatus && DELIVERY === packageType) {
        actualPackageStatus =INDUCTED;
    }
    switch (actualPackageStatus.toUpperCase()) {
        case DWELL_PACKAGE_STATUS.INDUCTED:
        case DWELL_PACKAGE_STATUS.INDUCT:
            return "theme";
        case DWELL_PACKAGE_STATUS.STOWED:
        case DWELL_PACKAGE_STATUS.SORT:
            return "success";
        case DWELL_PACKAGE_STATUS.INDUCT_PENDING:
            return "neutral";
        default:
            return "warning";
    }
}
export function emptyStringToHyphen(param) {
    return param || "-";
};
export function getFormattedMessage(id, defaultMessage) {
    return <FormattedMessage id ={id} defaultMessage={defaultMessage}/>
}
export function getFormattedMessageForState(pckgState, leg) {
    let state = getDwellPackageStatus(pckgState)
    if (AT_STATION === state && DELIVERY === leg) {
        return "Inducted"
    } else if (AT_STATION === state) {
        return "Manifested"
    }
    const actualScanPackageState = ActualScanToPackageStateMapping[state] ? ActualScanToPackageStateMapping[state] : state ;
    return emptyStringToHyphen(actualScanPackageState)
}
export function getDwellPackageStatus(param) {
    if (checkInductStatus(param)) {
        return INDUCT_PENDING;
    }
    return param;
};
export function checkInductStatus(argument) {
    const lowerArgument = argument.toLowerCase();
    return PACKAGE_STATUSES_PENDING_INDUCT.some(status => status.toLowerCase() === lowerArgument);
};

export function getDwellPackageStatusFromPckStatusAndPckType (packageStatus, packageType) {

    let actualPackageStatus = getDwellPackageStatus(packageStatus)
    if ((AT_STATION === packageStatus && DELIVERY === packageType) || INDUCT === packageStatus){
        actualPackageStatus =INDUCTED;
    }
    if (actualPackageStatus === SORT) {
        actualPackageStatus = STOWED;
    }

    return actualPackageStatus.toString()
};

export function naIfDefault(value) {
    return !value || value === "..." ? "NA" : value;
}

export function getCurrentTimezoneOffset() {
    return new Date().getTimezoneOffset() * 60 * 1000;
}

export function searchAssociateRequestOption(lastUpdatedTime) {
    let currentEpochMillis = lastUpdatedTime ? new Date(lastUpdatedTime).getTime() : new Date().getTime();
    let localCurrentEpochMillis = currentEpochMillis - getCurrentTimezoneOffset();
    let currentDate = new Date(localCurrentEpochMillis).toISOString().substr(0, 10);
    return {
        "type": "ASSOCIATE",
        "startDate": new Date(currentDate).getTime() + (getCurrentTimezoneOffset()),
        "endDate": new Date(currentDate).getTime() + (getCurrentTimezoneOffset()) + (60 * 60 * 24 * 1000)
    }
}
export function fetchAssociate(searchPath, lastProcessedByAssociateName, lastUpdatedTime) {
    return lastProcessedByAssociateName ?
    (validateEmail(lastProcessedByAssociateName) ? 
    (<Link href = {searchPath.concat(lastProcessedByAssociateName)
        .concat("/" + JSON.stringify(searchAssociateRequestOption(lastUpdatedTime)))}>{lastProcessedByAssociateName}</Link>)
    : lastProcessedByAssociateName
    )
    : '-';
}

export function getStationCode() {
    const params = new URLSearchParams(window.location.search);
    const nodeId =  params.get('stationCode');
    return nodeId ? {stationCode: nodeId}:{};
}

export async function getOperatorAlias() {
    const employeeName = await getEmployeeName();
    let operatorAlias = JSON.parse(employeeName.name) || "";
    if (operatorAlias.endsWith(AMAZON_SUFFIX)) {
        operatorAlias = operatorAlias.substring(0, operatorAlias.length - AMAZON_SUFFIX.length);
    }
    return operatorAlias;
}

export function isDevelopmentEnv(){
    return process.env.NODE_ENV !== ENVIRONMENT.PROD && process.env.NODE_ENV !== ENVIRONMENT.TEST;
}

export function getStage() {
    if (isDevelopmentEnv()) {
        return STAGE.DEV;
    }
    const hostname = window.location.hostname || window.location.href;
    if (hostname.includes(INTEG) || hostname.includes(BETA)) {
        return STAGE.BETA;
    } else if (hostname.includes(MASTER) || hostname.includes(GAMMA)) {
        return STAGE.GAMMA;
    } else {
        return STAGE.PROD;
    }
}

export function getServiceType(serviceType) {
    return serviceType ? ( Array.isArray(serviceType) ? serviceType.join(", ") : serviceType) : "";
}

export function convertArrayToString(arrayData) {
    return arrayData ? ( Array.isArray(arrayData) ? arrayData.join(", ") : arrayData) : "";
}

export function getStartTime() {
    const current = { hours: getHours(new Date()), minutes: getMinutes(new Date()) };
    const absolute = { hours: 19, minutes: 30 };
    if (current.hours > absolute.hours ||
        (current.hours === absolute.hours && current.minutes >= absolute.minutes) ) {
            return add(startOfToday(), absolute)
    }
    return add(startOfYesterday(), absolute);
}

export function getCurrentTime() {
    return new Date();
}

export function getRelativeTime(timestamp, suffix='ago') {
    if(isValid(timestamp)) {
        return `${formatDistance(new Date(timestamp), new Date(), {locale: getDateLocale()})} ${suffix}`;
    }
    return ``;
}

export function getDateDistance(timestamp) {
    if(isValid(timestamp)) {
        return Math.floor((new Date() - new Date(timestamp))/(1000*60*60*24));
    }

    return 0;
}

export function getRelativeDate(timestamp) {
    if(isValid(timestamp)) {
        return `${new Date(timestamp)}`;
    }
    return ``;
}

export function getFormattedDate(pattern) {
    return format(new Date(), pattern);
}

export function filterObject(initial, keys) {
    return keys.reduce((obj, key) => ({ ...obj, [key]: initial[key] }), {})
}

export function URIEncoder(params) {
    return Object
    .entries(params || {})
    .reduce((obj, [key, value]) => 
    (Array.isArray(value) 
    ? [...obj, ...value.map(v => [`${key}[]`, v])] 
    : [...obj, [key, value]]), [])
    .map(([key, val]) =>
     `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
     .join('&');
}

export function withRefreshTime(callback, refreshTime) {
    let repeat = setInterval(callback, refreshTime);
    callback();
    return repeat;
}

/**
 * This method return the refresh interval in milliseconds
 * @returns {number}
 */
export function getRefreshInterval() {
    return 60 * 1000;
}

/**
 * This method publishes the metric by station code.
 * @param args
 */
function publishMetricsByNode(args) {
    let stationCodeSuffix = UNDERSCORE + getStationName();
    args.metricName += stationCodeSuffix;
    publishToCSM(args)
}

export function logOperationalMetric(startTime, processName, metricName, isSuccess) {
    if (isSuccess === true) {
        let args = {
            startTime: startTime,
            processName: processName,
            metricName: metricName
        };
        publishToCSM(args);
        publishMetricsByNode(args);
    } else {
        publishCountToCSM({
            processName: processName,
            metricName: metricName + ERROR
        })
    }
}
 
function publishToCSM(args) {
    let metricValue = getTimeDiff(args.startTime);
    const countMetricReq = publishClientSideMetricsRequest(args.processName, args.metricName, 1, metricTypeEnum.COUNT);
    const timeMetricReq = publishClientSideMetricsRequest(args.processName, args.metricName, metricValue, metricTypeEnum.TIME);
    publishClientSideMetrics(countMetricReq);
    publishClientSideMetrics(timeMetricReq);
}
 
export function publishCountToCSM(args) {
    const countMetricReq = publishClientSideMetricsRequest(args.processName, args.metricName , 1, metricTypeEnum.COUNT);
    publishClientSideMetrics(countMetricReq);
}

export function publishCustomCountToCSM(args) {
    const countMetricReq = publishClientSideMetricsRequest(args.processName, args.metricName , args.metricValue, metricTypeEnum.COUNT);
    publishClientSideMetrics(countMetricReq);
}
 
function getTimeDiff(time) {
    return Date.now() - time ;
}
 
const metricTypeEnum = {
    COUNT: "count",
    TIME: "time"
};

const ERROR = "Error";
const UNDERSCORE = "_";
 
function publishClientSideMetricsRequest(processName, metricName, metricValue, metricType) {
    return {
        processName: processName,
        metricName: metricName,
        metricValue: metricValue,
        metricType: metricType
    }
}

export function getStationName() {
    let params = new URLSearchParams(window.location.search);
    return params.get('stationCode');
}

export const METRIC_PROCESS_NAME_SCRUB = "SCC_PACKAGE_SCRUB";

export function subtractDaysFromCurrentDate(amount) {
    return subDays(getCurrentTime(), amount);
}

export function isINRegion() {
    return REGION.IN === getRegion();
}

export function isFERegion() {
    return REGION.FE === getRegion();
}

export function isFilterConfigured(filter, page) {
    switch(filter) {
        case "lastUpdatedStart":
        case "lastUpdatedEnd":
            return (isINRegion() || page === "AGEING")
        default:
            return true
    }
}

export function getRegion() {
    let location = window.location.href;
    if (location.includes("package-summary-in")) {
        return REGION.IN;
    } else if (location.includes("package-summary-eu")) {
        return REGION.EU;
    } else if (location.includes("package-summary-na")) {
        return REGION.NA;
    } else if (location.includes("package-summary-fe")) {
        return REGION.FE;
    }
    return REGION.NA;
}

/**
 * Returns the current time in format [ MonthName dd, YYYY, HH:MM:SS GMT+__:__ ]
 * This pattern may slightly change according to current locale.
 * @param locale
 * @return string
 */
export const getCurrentFormattedTime = (locale) => {
    const currentDate = new Date();
    return new Intl.DateTimeFormat(locale, DATE_TIME_FORMAT).format(currentDate)
}

/*
 * Function to convert scheduledDeliveryWindowUTC timeString to stationTimeZone
 */
export function getLocalTimeDeliveryWindowFromUtcDeliveryWindow(utcDeliveryWindow, stationTimeZone) {
    var timeZone = stationTimeZone ? oldTimeZoneMapper(stationTimeZone) : undefined;
    return getLocalTimeStringFromUtcTimeString(utcDeliveryWindow.split("-")[0], timeZone) + '-' +
    getLocalTimeStringFromUtcTimeString(utcDeliveryWindow.split('-')[1], timeZone)
}

/*
* Returns if string is empty or not
*/
export function isNonEmptyString (string) {
   return  string !== "" && string !== null && string !== undefined
}

/*
* Returns true if the list exists and is not empty
*/
export function isNonEmptyList(list) {
    return Array.isArray(list) && list.length !== 0;
}

function getLocalTimeStringFromUtcTimeString (utcTimeString, timeZone) {
    const utcHour = parseInt(utcTimeString.split(':')[0], 10);
    const utcMinutes = parseInt(utcTimeString.split(':')[1], 10);
    const currentDateWithUtcTime = new Date().setHours(utcHour, utcMinutes, 0, 0);
    const timeZoneOffset = -(getTimezoneOffset(new Date(), timeZone));
    const currentDateWithLocalTime = new Date(currentDateWithUtcTime + timeZoneOffset);
    let hour = currentDateWithLocalTime.getHours().toString();
    if (hour.length <= 1) {
        hour = '0' + hour;
    }
    let minutes = currentDateWithLocalTime.getMinutes().toString();
    if (minutes.length <= 1) {
        minutes += '0';
    }
    return hour + ':' + minutes;
}

/*
* To get station timezone offset
 */
function getTimezoneOffset(date, tz) {
    const a = date.toLocaleString("ja", {timeZone: tz}).split(/[/\s:]/);
    a[1]--;
    const t1 = Date.UTC.apply(null, a);
    const t2 = new Date(date).setMilliseconds(0);
    return t2 - t1;
}

/**
 * This function check if provided url is a valid URL or not
 * @param issueLink
 * @returns {boolean}
 */
export function isIssueLinkValid(issueLink) {
    if (issueLink === undefined || issueLink === "")
        return false;

    return !((!issueLink) ||
        ((issueLink.indexOf("tt.amazon.com/") === -1) &&
            (issueLink.indexOf("http://tt.amazon.com/") === -1) &&
            (issueLink.indexOf("https://tt.amazon.com/") === -1) &&
            (issueLink.indexOf("http://t.corp.amazon.com/") === -1) &&
            (issueLink.indexOf("https://t.corp.amazon.com/") === -1) &&
            (issueLink.indexOf("issues.amazon.com/") === -1) &&
            (issueLink.indexOf("http://issues.amazon.com/") === -1) &&
            (issueLink.indexOf("https://issues.amazon.com/") === -1) &&
            (issueLink.indexOf("sim.amazon.com/") === -1) &&
            (issueLink.indexOf("http://sim.amazon.com/") === -1) &&
            (issueLink.indexOf("https://sim.amazon.com/") === -1)));
}

/**
 * This function check if provided url is a valid URL or not
 * @param issueLink
 * @returns {boolean}
*/
export function extractIdFromLink(issueLink) {
    let possibleLinks = ["http://tt.amazon.com/", "https://tt.amazon.com/", "tt.amazon.com/",
        "http://t.corp.amazon.com/", "https://t.corp.amazon.com/", "http://issues.amazon.com/", "https://issues.amazon.com/",
        "issues.amazon.com/", "http://sim.amazon.com/", "https://sim.amazon.com/", "sim.amazon.com/"];

    let ticketId = issueLink;
    for (let index in possibleLinks) {
        ticketId = ticketId.replace(possibleLinks[index], "");
    }

    return ticketId.replace("/", "");
}

let USE_CASE_CODE_TO_RESOURCE = new Map();
USE_CASE_CODE_TO_RESOURCE.set(UseCaseCode.PACKAGE_NOT_STAGED_HENCE_REMOVED_FROM_DRIVERS_ITINERARY, "usecase.package_not_staged_hence_removed_from_drivers_itinerary")

export function getStringForUseCase(useCase) {
    if(USE_CASE_CODE_TO_RESOURCE.has(useCase)) {
        return USE_CASE_CODE_TO_RESOURCE.get(useCase);
    }
    return useCase;
}

/**
 * This function splits the input list in small chunks of chunk_size
 * @param input
 * @param chunk_size
 * @returns {*}
 */
export function splitScannableIdsIntoBatches(input, chunk_size) {
    var results = [];
    while (input.length) {
        results.push(input.splice(0, chunk_size));
    }
    return results;
}

/**
 * Handles exception while reading from localStorage
 * @param key
 */
export function getFromLocalStorage(key) {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

/**
 * Handles exception while writing into localStorage
 * @param key
 * @param value
 */
export function saveIntoLocalStorage(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
    }
}

/**
 * Saves filter state into local storage along with current station code.
 * It also translates the newState into JSON.
 * @param newState
 */
export function saveFilterState(filterKey, newState) {
    try {
        const record = JSON.stringify({data: newState, stationCode: getStationCode()});
        saveIntoLocalStorage(filterKey, record);
    } catch (e) {
    }
}

/**
 * Reads filter state from local storage by translating it into Json.
 * If current station code is different the persisted one, it returns null.
 * @param filterKey
 */
export function readFilterState(filterKey) {
    try {
        const json = getFromLocalStorage(filterKey) || null;
        if (json) {
            const record = JSON.parse(json);
            if (record.stationCode === getStationCode()) {
                return record.data;
            }
        }
    } catch (ignored) {
    }
    return null;
}


// WEBPACK FOOTER //
// ./src/util/CommonUtils.js